import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTheme as useWidgetTheme } from "styled-components";

import { useSelector } from "react-redux";
import {
  StyledBotContainer,
  StyledFloatingButtonWrap,
} from "../Styles/StyledScreens";

import { HostWindowCommService } from "../../Services/IframeHostService/HostWindowCommService";
import { IEvents, OEvents } from "../../Services/IframeHostService/models";
import { useAppDispatch, useAppSelector } from "../../Store";
import { useBotConfigStore } from "../../Store/Slices/ChatbotSlices/BotConfigSlice";
import {
  botInfoStore,
  setIsOpenedFirstTime,
  setOpenStatus,
  setBotToFullScreen,
  setBotToMobileView,
} from "../../Store/Slices/socket/BotInfoSlice";
import {
  deviceSizeStore,
  setDeviceSize,
} from "../../Store/Slices/socket/DeviceSizeSlice";

import MainUI from "../../socket/screens/MainUI";
import useSocket from "../hooks/useSocket";
import { PreviewType } from "../utils/Enum";
import CalloutMessageScreen from "./CalloutMessageScreen";
import SpeedDialButton from "./SpeedDialButton";
import {
  setExecuteConnectSocketEventWithConnectUser,
  setExecuteConnectSocketEventWithReConnectUser,
  socketStatusStore
} from "../../Store/Slices/socket/SocketSlice";
import useTimer from "../hooks/useTimer";
import {getWidgetPositionConfig } from "../utils/utils";
import { DEVICE_SIZE } from "../../Customization/BreakPoints";
import {
  CUSTOM_HEIGHT_BOUNDS,
  CUSTOM_WIDTH_BOUNDS,
  clampCustomSize,
  resolveDockedFrameWidth,
  Surface,
  WIDGET_FRAME_GUTTER,
  WIDGET_SIZE_PRESETS,
} from "../../Models/ChatbotConfiguration";

interface Props { }

const Home: React.FC<Props> = () => {
  const containerRef = useRef<any>(null);
  const floatingButtonRef = useRef<any>(null);
  const autoOpenTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useAppDispatch();
  const { timer } = useTimer(1500);
  const { botConfig } = useAppSelector(useBotConfigStore);
  const IframeDimensions = useSelector(deviceSizeStore);
  const [mobileView, setMobileView]= useState<boolean>(false)
  const { chatOpened, isOpenedFirstTime, widgetPreviewType, isBotSetToFullScreen , isBotSetToMobileView, isUserExpanded} = useSelector(botInfoStore);
  const [fullscreen, setFullscreen] = React.useState<boolean>(false);

  //* do not remove this import
  const { socket } = useAppSelector(socketStatusStore);
  const { height, width, actualWidth, actualHeight } =
    useSelector(deviceSizeStore);
  // actualWidth is the raw device width the host reported, before any preset
  // capping — 0 until the first resize handshake, so an unconfirmed reading
  // isn't treated as mobile.
  const isMobileViewport = !!actualWidth && actualWidth < DEVICE_SIZE.tablet;

  const [clientViewPortSize, setClientViewPortSize] = React.useState<any>(0);

  let commService = useMemo(() => {
    return HostWindowCommService.getInstance();
  }, []);

  const chatbotAutoOpen = useMemo(
    () => botConfig?.config?.always_open ?? botConfig?.config?.auto_open ?? false,
    [botConfig]
  );

  const chatWidgetAutoOpen = useMemo(
    () => botConfig?.config?.auto_open_enabled ?? false,
    [botConfig]
  );

  const chatbotAutoOpenDelay = useMemo(() => {
    const delayInSeconds = botConfig?.config?.auto_open_delay ?? 8; // Default 8 seconds
    return delayInSeconds * 1000; // Convert seconds to milliseconds
  }, [botConfig]);

  // Resolved once in WidgetApp: workspace on the rollout AND the bot's own
  // "Enable Modern Style" on. Read from the theme so sizing stays in step with
  // the styling rather than deciding it a second way. Without this gate every
  // existing widget would grow from 376x610, since geta-core defaults
  // widget_size to "standard" for every config.
  const widgetTheme: any = useWidgetTheme();
  const isNewUi = !!widgetTheme?.industry_style?.enabled;

  const widgetSizePreset = useMemo(() => {
    if (!isNewUi) return null;
    const size = (botConfig?.config?.widget_size ?? "standard") as keyof typeof WIDGET_SIZE_PRESETS;
    return WIDGET_SIZE_PRESETS[size] ?? WIDGET_SIZE_PRESETS.standard;
  }, [botConfig, isNewUi]);

  // Master applied no bounds to these; only the redesign clamps them.
  const resolveCustomSize = (value: unknown, bounds: { min: number; max: number }) =>
    isNewUi ? clampCustomSize(value, bounds) : (Number(value) || null);

  const effectiveSurface: Surface = isUserExpanded ? "expanded" : "docked";

  const applyViewportSize = (viewportSize: { width?: number; height?: number } | undefined) => {
    if (!viewportSize?.width || !viewportSize?.height) return;

    setClientViewPortSize(viewportSize);

    let newHeight;

    if (viewportSize.width < 768) {
      newHeight = viewportSize.height;
    } else {
      if (viewportSize.height < 500) {
        newHeight = viewportSize.height;
      } else {
        // A fixed 751px cap used to apply here once the window got tall
        // enough, so the panel stopped growing past that point no matter
        // how much more room the window actually had. Browser zoom
        // shrinks/grows this CSS-pixel viewport height for the same
        // physical screen, so which side of that cap a given screen
        // landed on — and therefore whether the panel's height tracked
        // the window or was pinned at a stale fixed number — depended on
        // the zoom level rather than the window itself. Always scaling
        // with the window keeps it responsive at any zoom or window size.
        newHeight = viewportSize.height - 100;
      }
    }

    let requestedWidth;
    if (!isNewUi) {
      // Exactly what master asked for — no custom-width branch, no preset.
      requestedWidth = viewportSize.width > 768 ? 376 : viewportSize.width;
    } else if (viewportSize.width < 768) {
      requestedWidth = viewportSize.width;
    } else if (resolveCustomSize(botConfig?.config?.custom_width, CUSTOM_WIDTH_BOUNDS)) {
      requestedWidth = Math.min(
        (resolveCustomSize(botConfig?.config?.custom_width, CUSTOM_WIDTH_BOUNDS) as number) +
          WIDGET_FRAME_GUTTER,
        viewportSize.width
      );
    } else {
      requestedWidth = (widgetSizePreset?.width ?? 360) + WIDGET_FRAME_GUTTER;
    }

    // This store always holds the DOCKED size, never the expanded one.
    // frameWidth/frameHeight read clientViewPortSize directly while expanded,
    // so storing the viewport here would only leak: on collapse the docked
    // width would still be the full viewport, leaving the panel pinned to one
    // edge of a page-wide iframe instead of returning to its corner.
    let temp = {
      width: requestedWidth,
      height: newHeight,
      // Presets are sized deliberately — don't let the legacy 376 cap undo them.
      usePresetWidth: isNewUi && viewportSize.width >= 768,
      // The host's real, uncapped viewport width — see the note on
      // rawViewportWidth in DeviceSizeSlice. Without this, actualWidth (and
      // therefore isMobileViewport) read the legacy-capped 376 instead of the
      // host's real screen size, permanently misreading any desktop legacy
      // widget as a mobile viewport.
      rawViewportWidth: viewportSize.width,
    };

    dispatch(setDeviceSize(temp));
  };

  // The last real viewport reading the host told us about, kept outside
  // Redux/clientViewPortSize so the zoom-correction effect below can scale
  // from the true last-known host size instead of compounding its own
  // previous corrections on every zoom step.
  const lastHostViewportRef = useRef<{ width: number; height: number } | null>(null);
  const lastPixelRatioRef = useRef<number>(window.devicePixelRatio || 1);

  // Resize event listener
  useEffect(() => {
    const handleParentWindowResize = (event: any) => {
      const viewportSize = event?.data?.data?.viewportSize;
      if (viewportSize?.width && viewportSize?.height) {
        lastHostViewportRef.current = { width: viewportSize.width, height: viewportSize.height };
        lastPixelRatioRef.current = window.devicePixelRatio || 1;
      }
      applyViewportSize(viewportSize);
    };

    // Register the event listener when the component mounts
    commService.registerEventListener(
      IEvents.PARENT_WINDOW_RESIZE,
      handleParentWindowResize
    );
  });

  // The host only re-measures its own viewport and posts PARENT_WINDOW_RESIZE
  // on a "resize" event on ITS page. Browser zoom changes the CSS-pixel size
  // of that page, but doesn't reliably fire "resize" there in every browser —
  // so on a zoom-only change the host may never send a fresh viewportSize,
  // and the widget keeps rendering at the last (now stale) size.
  //
  // devicePixelRatio is the one reading that is both (a) available inside
  // this iframe without asking the host, and (b) driven purely by the
  // browser's zoom/display scale — unlike this iframe's own
  // window.innerWidth/innerHeight, which the HOST sets via CHATBOT_RESIZE
  // and would create a resize -> postMessage -> resize feedback loop if used
  // here instead (that loop is what caused the widget to blink previously).
  //
  // On a devicePixelRatio change we don't re-measure anything — we scale the
  // last real host viewport reading by how much the ratio moved, so a zoom
  // step that (for example) shrinks the effective page by 10% shrinks the
  // panel's target size by the same 10%, without ever touching this iframe's
  // own DOM size.
  useEffect(() => {
    const handlePixelRatioChange = () => {
      const previousRatio = lastPixelRatioRef.current || 1;
      const currentRatio = window.devicePixelRatio || 1;
      const baseline = lastHostViewportRef.current;

      if (!baseline || currentRatio === previousRatio) return;

      const scale = previousRatio / currentRatio;
      lastPixelRatioRef.current = currentRatio;
      lastHostViewportRef.current = {
        width: baseline.width * scale,
        height: baseline.height * scale,
      };

      applyViewportSize(lastHostViewportRef.current);
    };

    // matchMedia on the current ratio fires once when the ratio actually
    // changes; re-subscribing at the new ratio each time catches every step
    // of a multi-step zoom instead of only the first.
    const mql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    mql.addEventListener?.("change", handlePixelRatioChange);

    return () => {
      mql.removeEventListener?.("change", handlePixelRatioChange);
    };
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuickChat = () => {
    commService.postMessage({
      event_type: OEvents.CHATBOT_OPEN,
      data: { open: chatOpened ? true : false },
    });
    dispatch(setOpenStatus(!chatOpened));

    if (!isOpenedFirstTime) {
      dispatch(setIsOpenedFirstTime(true));
      // Clear auto-open timer when user manually opens the chatbot
      if (autoOpenTimerRef.current) {
        clearTimeout(autoOpenTimerRef.current);
        autoOpenTimerRef.current = null;
      }
    }
  };

  // The host only tells us its viewport on PARENT_WINDOW_RESIZE. If that has
  // not arrived yet, expanding would silently keep the docked size, so fall
  // back to window.screen — an upper bound we can read cross-origin. That's
  // monitor resolution, not the browser window, so it overshoots whenever the
  // host's window is smaller than the screen (tiled/snapped, not maximized) —
  // only used as a last resort, below actualWidth/actualHeight.
  const expandedFallback = useMemo(
    () => ({
      width: Math.min(1200, (window.screen?.availWidth || 1200) - 40),
      height: Math.min(920, (window.screen?.availHeight || 920) - 40),
    }),
    []
  );

  const frameWidth = useMemo(() => {
    if (effectiveSurface === "expanded") {
      // clientViewPortSize is the freshest reading but only exists after a
      // PARENT_WINDOW_RESIZE; actualWidth is the host's real viewport width
      // reported at CHATBOT_START, so it's already correct on the very first
      // Expand click — both are real viewport reads, unlike the screen-based
      // fallback below, so either is safe to trust over it.
      return clientViewPortSize?.width || actualWidth || expandedFallback.width;
    }
    return resolveDockedFrameWidth({
      preset: widgetSizePreset,
      customWidth: resolveCustomSize(
        botConfig?.config?.custom_width,
        CUSTOM_WIDTH_BOUNDS
      ),
      storeWidth: width,
      isMobileViewport,
      actualWidth,
    });
   }, [width,botConfig,effectiveSurface,clientViewPortSize,expandedFallback,widgetSizePreset,isMobileViewport,actualWidth]);

  const frameHeight = useMemo(() => {
    const autoHeight = botConfig?.config?.auto_height ?? false;
    const modifiedFinalValue = height - (botConfig?.config?.auto_height_limiting_number ?? 0);

    if (effectiveSurface === "expanded") {
      return clientViewPortSize?.height || actualHeight || expandedFallback.height;
    }

    // If window height is less than 620px, make frameHeight equal to window height.
    // Presets/custom height are also skipped on a confirmed mobile viewport —
    // height<=620 alone misses a tall phone in portrait (e.g. 844px), which
    // would otherwise still get the Tall preset's 858px and overflow.
    if (height <= 620 || isMobileViewport) {
      return height;
    }

    if (!isNewUi) {
      // Master had no custom-height branch and a fixed 610 ceiling.
      const legacyMax = autoHeight ? modifiedFinalValue : 610;
      return legacyMax ?? 610;
    }

    const customHeight = resolveCustomSize(
      botConfig?.config?.custom_height,
      CUSTOM_HEIGHT_BOUNDS
    );
    if (customHeight) {
      return customHeight;
    }

    const presetHeight = widgetSizePreset ? widgetSizePreset.height + 78 : 610;
    const maxHeight = autoHeight ? modifiedFinalValue : presetHeight;
    return maxHeight ?? 610;
   }, [height,botConfig,widgetSizePreset,effectiveSurface,clientViewPortSize,expandedFallback,isMobileViewport,actualHeight]);

  const handleOpenStateIframeDimensions = () => {
    // Docked leaves a gutter for the launcher and the panel's shadow; expanded
    // is a pop-out and wants everything it asked for.
    const gutter = effectiveSurface === "expanded" ? { w: 0, h: 0 } : { w: 16, h: 8 };
    const wh2 = {
      width: frameWidth - gutter.w,
      height: frameHeight - gutter.h,
      state: "open",
    };
    commService.postMessage({
      event_type: OEvents.CHATBOT_RESIZE,
      data: wh2,
    });
  };

  useEffect(() => {
    if (chatOpened) {
      handleOpenStateIframeDimensions();
    }
    // frameWidth/frameHeight are what actually gets sent to the host as the
    // iframe's requested size (handleOpenStateIframeDimensions reads them
    // via closure) — they used to be missing from this list, so a resize
    // request only went out when clientViewPortSize/chatOpened/surface
    // changed. If frameHeight settled on its real value (e.g. once
    // widgetSizePreset loaded) AFTER that, the host's <iframe> was left at
    // whatever smaller size the earlier request asked for, while the panel
    // inside kept rendering at its real (larger) height — the excess then
    // spilled out past the iframe's own top edge, taking the header with
    // it, since it never got a follow-up resize request telling it to grow.
  }, [clientViewPortSize, chatOpened, effectiveSurface, frameWidth, frameHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClosedStateIframeDimensions = () => {
    if (floatingButtonRef?.current) {
      let { width: closedDivWidth, height: closedDivHeight } =
        floatingButtonRef.current.getBoundingClientRect();

      let newCalculatedWidth = closedDivWidth + 16 > 80 ? floatingButtonRef.current.offsetWidth : 80;
      let newCalculatedHeight =
        closedDivHeight + 16 > 84 ? closedDivHeight : 80;

      setTimeout(() => {
        const wh = {
          width: newCalculatedWidth,
          height: newCalculatedHeight,
          state: "close",
        };
        commService.postMessage({
          event_type: OEvents.CHATBOT_RESIZE,
          data: wh,
        });
      }, 100);
    }
  };

  useEffect(() => {
    if (chatOpened && !isOpenedFirstTime) {
      dispatch(setIsOpenedFirstTime(true));
    }
    if (chatOpened) {
      handleOpenStateIframeDimensions();
    } else {
      handleClosedStateIframeDimensions();
    }
  }, [chatOpened, isOpenedFirstTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const { disconnectSocketEvent, connectSocketEventWithReConnectUser, connectSocketEventWithConnectUser } = useSocket();

  useEffect(() => {
    // connectSocketEvent();
    dispatch(setExecuteConnectSocketEventWithReConnectUser(connectSocketEventWithReConnectUser));
    //@ts-ignore
    dispatch(setExecuteConnectSocketEventWithConnectUser(connectSocketEventWithConnectUser));
    return () => {
      disconnectSocketEvent();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      widgetPreviewType === PreviewType.getaWidgetPreview || chatbotAutoOpen
    ) {
      setTimeout(() => {
        dispatch(setOpenStatus(true));   
        
      }, 100); // DO NOT remove settimeout //
    }
  }, [widgetPreviewType]); // eslint-disable-line react-hooks/exhaustive-deps


  // Seperate useEffect for auto open delay because it affects the widget preview type
  useEffect(() => {
    if (chatWidgetAutoOpen && !isOpenedFirstTime) {
      autoOpenTimerRef.current = setTimeout(() => {
        dispatch(setOpenStatus(true));
        autoOpenTimerRef.current = null; // Clear the reference after timer fires
      }, chatbotAutoOpenDelay); // DO NOT remove settimeout //

    }

    // Cleanup function to clear timer if component unmounts or dependencies change
    return () => {
      if (autoOpenTimerRef.current) {
        clearTimeout(autoOpenTimerRef.current);
        autoOpenTimerRef.current = null;
      }
    };
  }, [chatWidgetAutoOpen, isOpenedFirstTime, chatbotAutoOpenDelay]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isBotSetToFullScreen) {
      setTimeout(() => {
        dispatch(setBotToFullScreen(true));
        dispatch(setOpenStatus(true));
        setFullscreen(true);
      }, 100);
    }
  }, [isBotSetToFullScreen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    if(isBotSetToMobileView){
      setTimeout(() => {
        dispatch(setBotToMobileView(true));
        dispatch(setOpenStatus(true));
        setMobileView(true);
      }, 100);
    }
  },[isBotSetToMobileView])

  const widgetPositionConfig = useMemo(() => {
    return getWidgetPositionConfig({config:botConfig.config, IframeDimensions:IframeDimensions});
  }, [IframeDimensions, botConfig]);

  if (timer > 0) return null;

  return (
    <StyledBotContainer
      devicewidth={frameWidth}
      isFullScreen={fullscreen}
      deviceheight={frameHeight}
      chatopened={chatOpened ? 1 : 0}
      id="bot-container"
      ref={containerRef}
      WIDGET_POSITION={widgetPositionConfig}
      isTooltipVisible={!isOpenedFirstTime}
      surface={effectiveSurface}
      isMobileViewport={isMobileViewport}
    >
      <MainUI
        isFullScreen={fullscreen}
        isMobileView={mobileView}
        surface={effectiveSurface}
      />

      <StyledFloatingButtonWrap
        chatopened={chatOpened ? 1 : 0}
        tooltipPosition={botConfig.config?.tooltip?.position}
        WIDGET_POSITION={widgetPositionConfig}
        isTooltipVisible={isOpenedFirstTime}
        ref={floatingButtonRef}
        hideFloatingButton={botConfig.config?.hide_floating_Button ?? false}
      >
        {botConfig.config?.tooltip?.enabled &&
          !chatOpened &&
          !isOpenedFirstTime ? (
          <CalloutMessageScreen
            handleQuickChat={handleQuickChat}
            handleClosedStateIframeDimensions={
              handleClosedStateIframeDimensions
            }
          />
        ) : null}
        <SpeedDialButton handleQuickChat={handleQuickChat} />
      </StyledFloatingButtonWrap>
    </StyledBotContainer>
  );
};

export default memo(Home);
